import { useCallback, useEffect, useId, useState } from "react";
import { ExternalLink } from "lucide-react";
import api, { getErrorMessage } from "@/lib/api";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { schedulerSignInTarget, schedulingLabel } from "@/lib/schedulerSignIn";
import { SettingsRow, SettingsSection, Toggle } from "./SettingsSection";
import ConnectedClasses from "./ConnectedClasses";
import OrganizationSchedulerSetup from "./OrganizationSchedulerSetup";
import SchedulerIdentityLink from "./SchedulerIdentityLink";

const actionClass = "inline-flex items-center justify-center gap-2 rounded-full border border-[var(--studio-divider)] px-4 py-2.5 font-sans text-sm text-[var(--text-primary)] transition-colors duration-200 hover:bg-[var(--studio-overlay)] active:scale-95 disabled:opacity-50";

/** What a blocked activation means for the person reading the page.
 *
 * A connection rests in `shadow` until the reviewed operation cutover moves it
 * to `active`, and no route exposes that cutover, so each of these ends with
 * who to ask rather than something to click.
 */
const ACTIVATION_BLOCKERS = {
  independent_classes: { label: "Classes are ready",
    detail: "Hans uses Scheduler for your new classes. Other calendars remain separate. Family booking is closed." },
  setup_unfinished: { label: "Setup unfinished",
    detail: "Scheduling setup was started but never finished, so this business is not connected yet." },
  cutover_not_started: { label: "Not open yet",
    detail: "Hans still owns this organization's schedule. Moving classes, bookings and reminders to Kalver Scheduler is a reviewed cutover that Hans support runs for you." },
  cutover_in_progress: { label: "Cutover in progress",
    detail: "The scheduling cutover has started. It finishes once the existing schedule and reminders have drained, which Hans support completes." },
  publication_review_required: { label: "Waiting on its final review",
    detail: "The scheduling cutover is ready and waiting on its final publication review by Hans support." },
  paused: { label: "Scheduling is paused",
    detail: "Scheduling is paused. Reopening it needs a new publication review by Hans support." },
  unavailable: { label: "Scheduling is unavailable",
    detail: "Scheduling is unavailable for this business." },
};

const ACCESS_REVIEW = { label: "Your access needs review",
  detail: "Your scheduling access for this business needs review before you can use its calendar." };

/** Why this calendar cannot be used yet: a short status and the full reason.
 *  `null` once the connection is active and the reader's access is current. */
function blockedReason(connection) {
  if (connection.membership && connection.membership.status !== "active") return ACCESS_REVIEW;
  if (connection.status === "active") return null;
  return ACTIVATION_BLOCKERS[connection.activation_blocked_by] || ACTIVATION_BLOCKERS.unavailable;
}

/**
 * Hans chooses the business; its services, hours and payment settings open in Kalver.
 *
 * `unavailable` is what to render when scheduling is not offered at all (the
 * connection read 404s). Inside a longer page nothing is the right answer; a
 * page that holds only this section has to say why it is empty — and titles
 * it itself, so passes `title={null}`.
 */
export default function SchedulingSection({ organization = false, unavailable = null, title = "Scheduling" }) {
  const [connections, setConnections] = useState([]);
  const [offered, setOffered] = useState(true);
  const [selected, setSelected] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [setup, setSetup] = useState(null);
  const [classSetup, setClassSetup] = useState(null);
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [timezone, setTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "");
  const pickerId = useId();
  const client = organization ? orgApi : api;
  const errorMessage = organization ? getOrgErrorMessage : getErrorMessage;
  const prefix = organization ? "/org/scheduler" : "/scheduler";

  const load = useCallback(async (signal) => {
    try {
      const { data } = await client.get(`${prefix}/${organization ? "connection" : "connections"}`, { signal });
      if (signal?.aborted) return;
      const rows = organization ? (data?.connection ? [data.connection] : []) : (data?.connections || []);
      setConnections((previous) => previous.length || rows.length ? rows : previous);
      setLoaded(true);
      setSetup(data?.setup || null);
      setClassSetup(data?.class_setup || null);
      setSelected((previous) => rows.some((row) => row.connection_id === previous) ? previous : (rows.find((row) => row.business_id === new URLSearchParams(window.location.search).get("businessId")) || rows.find((row) => !row.org_id) || rows[0])?.connection_id || "");
      setError("");
    } catch (err) {
      if (signal?.aborted) return;
      if (err?.response?.status === 404) { setConnections([]); setLoaded(false); setError(""); setOffered(false); }
      else setError("Scheduling is unavailable. Please try again.");
    }
  }, [client, organization, prefix]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const connection = connections.find((row) => row.connection_id === selected);
  const canManage = organization || (!connection?.org_id && connection?.membership?.role === "owner");
  const available = connection && ["shadow", "active"].includes(connection.status) &&
    (organization || connection.membership?.status === "active");
  const blocked = connection ? blockedReason(connection) : "";
  const path = `${prefix}/connections/${encodeURIComponent(selected)}`;
  // Organizations read the server's reason; a personal studio keeps its own gate.
  const classesReady = organization ? Boolean(classSetup?.ready) : connection?.status === "active";
  const showSetup = loaded && !organization && (setup ? setup.status !== "connected" : !connection);

  const verifyEmail = async (confirm) => {
    setBusy(true); setError("");
    try {
      await api.post(`/auth/${confirm ? "verify-email" : "resend-verification"}`,
        confirm ? { email: setup.email, code } : { email: setup.email });
      if (confirm) { setCodeSent(false); setCode(""); await load(); }
      else setCodeSent(true);
    } catch (err) {
      setError(getErrorMessage(err, "Could not verify your email. Please try again."));
    } finally { setBusy(false); }
  };

  const setupScheduler = async () => {
    setBusy(true); setError("");
    try {
      await client.post(`${prefix}/setup`);
      await load();
    } catch (err) {
      setError(errorMessage(err, "Could not set up scheduling. Please try again."));
    } finally { setBusy(false); }
  };

  const saveTimezone = async () => {
    setBusy(true); setError("");
    try { await api.put("/auth/profile", { timezone }); await load(); }
    catch (err) { setError(getErrorMessage(err, "Could not save your timezone. Please try again.")); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (loaded && window.location.hash === "#scheduling") {
      document.getElementById("scheduling")?.scrollIntoView?.({ block: "start" });
    }
  }, [loaded]);

  const createOrganizationScheduler = async (details) => {
    setBusy(true); setError("");
    try {
      await orgApi.post("/org/scheduler/connection", details);
      await load();
    } catch (err) {
      const message = getOrgErrorMessage(err, "Could not finish scheduling setup. Please try again.");
      // A lost response may still have created the business. Read saved setup
      // before offering another submission; never invent a replacement.
      await load();
      setError(message);
    } finally { setBusy(false); }
  };

  const openScheduler = async (destination) => {
    setBusy(true); setError("");
    try {
      // An owner (of an organization's business, so not canManage here) is
      // refused Scheduler's my-calendar page; their calendars are under integrations.
      const ownCalendar = connection?.membership?.role === "owner" ? "integrations" : "my-calendar";
      const { data } = await client.post(`${path}/sign-in`, { destination: canManage ? (typeof destination === "string" ? destination : "dashboard") : ownCalendar });
      window.location.assign(schedulerSignInTarget(data, connection.business_id));
    } catch (err) {
      setError(errorMessage(err, "Could not open scheduling. Please try again."));
    } finally { setBusy(false); }
  };

  const setConsent = async (enabled) => {
    setBusy(true); setError("");
    try {
      const { data } = await client.post(`${path}/booking-consent`, { expected_version: connection.version, enabled });
      setConnections((rows) => rows.map((row) => row.connection_id === selected ? { ...row, ...data } : row));
    } catch (err) {
      // Keep the acknowledged setting. A changed business needs a fresh read.
      const message = errorMessage(err, "Could not update booking consent. Please try again.");
      if (err?.response?.status === 409) await load();
      setError(message);
    } finally { setBusy(false); }
  };

  if (!connection && !error && !loaded) return offered ? null : unavailable;
  return (
    <SettingsSection id="scheduling" title={title} testId="scheduling-settings" className="scroll-mt-24">
      {organization && loaded && <OrganizationSchedulerSetup key={JSON.stringify(setup)} setup={setup} connection={connection}
        busy={busy} onCreate={createOrganizationScheduler} onOpen={openScheduler} onRefresh={() => load()} />}
      {organization && loaded && <SchedulerIdentityLink onLinked={() => load()} />}
      {!organization && loaded && <SettingsRow title="Also an organization administrator?" desc="Link your personal and administrator profiles from your organization's Schedule settings to use both with one Scheduler account.">
        <a className={`${actionClass} mt-3`} href="/org/settings/schedule" target="_blank" rel="noopener noreferrer">Link profiles in organization settings</a>
      </SettingsRow>}
      {showSetup && <SettingsRow title="Scheduling for your own studio" last={!error && !connection}
        desc={setup?.message || "Set up your personal scheduling business, then open Kalver Scheduler to manage services, availability and bookings."}>
        {setup?.status === "timezone_required" && <form className="mt-4 space-y-3" onSubmit={(event) => { event.preventDefault(); saveTimezone(); }}>
          <label className="block text-sm text-[var(--text-primary)]">Scheduling timezone
            <input className="mt-2 block w-full rounded-xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-3"
              value={timezone} onChange={(event) => setTimezone(event.target.value)} placeholder="America/New_York" maxLength={80} />
          </label>
          <button type="submit" className={actionClass} disabled={busy || !timezone}>Save timezone</button>
        </form>}
        {(!setup || ["ready", "pending"].includes(setup.status)) && <button type="button" className={`${actionClass} mt-4`} disabled={busy} onClick={setupScheduler}>
          {busy ? "Setting up…" : setup?.status === "pending" ? "Finish setup" : "Set up scheduler"}<ExternalLink size={16} strokeWidth={1.5} />
        </button>}
        {setup?.status === "verification_required" && <div className="mt-4 space-y-3">
          <button type="button" className={actionClass} disabled={busy} onClick={() => verifyEmail(false)}>
            {codeSent ? "Resend verification code" : "Send verification code"}
          </button>
          {codeSent && <form onSubmit={(event) => { event.preventDefault(); verifyEmail(true); }} className="space-y-3">
            <p role="status" className="text-sm text-[var(--text-secondary)]">Enter the code sent to {setup.email}.</p>
            <label className="block text-sm text-[var(--text-primary)]">Email verification code
              <input className="mt-2 block rounded-xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-3"
                value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric" autoComplete="one-time-code" maxLength={6} />
            </label>
            <button type="submit" className={actionClass} disabled={busy || !/^\d{6}$/.test(code)}>Verify email</button>
          </form>}
        </div>}
        {setup && !["ready", "pending"].includes(setup.status) && <button type="button" className={`${actionClass} mt-3`} disabled={busy} onClick={() => load()}>Refresh scheduling</button>}
      </SettingsRow>}
      {connection && <>
        <SettingsRow title="Business" desc="Services, staff, payments and business hours are managed in the scheduler.">
          {connections.length > 1 ? <div className="mt-4">
            <label htmlFor={pickerId} className="mb-2 block font-sans text-sm text-[var(--text-secondary)]">Scheduling business</label>
            <select id={pickerId} value={selected} disabled={busy} onChange={(event) => { setSelected(event.target.value); setError(""); }}
              className="w-full rounded-xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-3 font-sans text-sm text-[var(--text-primary)]">
              {connections.map((row) => <option key={row.connection_id} value={row.connection_id}>{schedulingLabel(row)}</option>)}
            </select>
          </div> : <p className="mt-3 font-sans text-sm text-[var(--text-primary)]">{schedulingLabel(connection)}</p>}
          <p className="mt-2 font-sans text-sm text-[var(--text-tertiary)]">{blocked ? blocked.label : "Connected"}</p>
          <button type="button" className={`${actionClass} mt-4`} disabled={busy || !available} onClick={openScheduler}>
            {busy ? "Please wait…" : canManage ? "Open scheduler" : "Open my calendar"}<ExternalLink size={16} strokeWidth={1.5} />
          </button>
        </SettingsRow>
        {canManage && <SettingsRow title="Recording consent at booking"
          desc="Bookers can consent for themselves or as a parent or legal guardian. Each attendee has a separate choice."
          trailing={<Toggle label="Allow recording consent at booking" on={connection.consent_collection_enabled}
            disabled={busy || !available} onChange={setConsent} />} />}
        {/* Class setup follows schedule ownership, so it can be ready while booking
            activation is still blocked; the server's class reason wins when it has one. */}
        {canManage && (classesReady
          ? <ConnectedClasses key={selected} connection={connection} organization={organization} />
          : <SettingsRow title="Connected classes" last
              desc="Class seats, rosters and meeting dates are set up here once this business's scheduling is ready.">
              <p className="mt-3 font-sans text-sm text-[var(--text-primary)]">{classSetup?.message || blocked?.detail}</p>
            </SettingsRow>)}
        {!canManage && <SettingsRow last desc="Your organization manages the booking forms and shared business settings." />}
      </>}
      {error && <SettingsRow last><p role="alert" className="font-sans text-sm text-[var(--state-danger)]">{error}</p>
        {!(showSetup && setup && !["ready", "pending"].includes(setup.status)) && <button type="button" className={`${actionClass} mt-3`} disabled={busy} onClick={() => load()}>Refresh scheduling</button>}
      </SettingsRow>}
    </SettingsSection>
  );
}
